import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, CheckCircle2, PlayCircle, Award, ChevronRight } from "lucide-react";
import PageShell from "@/components/brand/PageShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function lockReasonLabel(reason) {
    switch (reason) {
        case "phase_locked": return "This phase is locked. Complete the previous phase's checkpoint quiz to unlock.";
        case "previous_module_incomplete": return "Complete the previous module first.";
        case "phase_modules_incomplete": return "Complete every module in this phase, then take the checkpoint quiz.";
        case "quiz_cooldown_active": return "You're in a 24-hour cooldown after your last quiz attempt.";
        case "subscription_inactive": return "Your subscription is inactive. Reactivate to resume the curriculum.";
        default: return "This content is locked.";
    }
}

function ModuleRow({ module, phase, currentUserActive, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const percent = module.total_lessons_count
        ? Math.round((module.completed_lessons_count / module.total_lessons_count) * 100)
        : 0;

    return (
        <div className="border-t" style={{ borderColor: "var(--st-line)" }} data-testid={`module-row-${module.id}`}>
            <button
                type="button"
                onClick={() => !module.locked && setOpen((v) => !v)}
                disabled={module.locked}
                className={`w-full flex items-center gap-4 py-4 px-2 text-left ${module.locked ? "opacity-70 cursor-not-allowed" : "hover:bg-silver-50"}`}
                data-testid={`module-toggle-${module.id}`}
                style={{ transition: "background-color 0.15s ease" }}
            >
                <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                     style={{ background: module.completed ? "#ECFDF3" : "var(--st-silver-100)" }}>
                    {module.locked ? (
                        <Lock size={14} strokeWidth={2} color="var(--st-silver-500)" />
                    ) : module.completed ? (
                        <CheckCircle2 size={16} strokeWidth={2} color="#067647" />
                    ) : (
                        <span className="text-xs font-semibold" style={{ color: "var(--st-navy)" }}>{module.order}</span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-serif text-base" style={{ color: "var(--st-navy)" }}>
                            {module.title}
                        </span>
                        {module.completed && (
                            <span className="tracking-caps text-[9px] font-bold px-1.5 py-0.5 rounded"
                                  style={{ background: "#ECFDF3", color: "#067647" }}>
                                Complete
                            </span>
                        )}
                    </div>
                    {module.locked ? (
                        <div className="text-xs mt-0.5" style={{ color: "var(--st-silver-500)" }}>
                            {lockReasonLabel(module.lock_reason)}
                        </div>
                    ) : (
                        <div className="text-xs mt-0.5" style={{ color: "var(--st-ink-muted)" }}>
                            {module.completed_lessons_count} of {module.total_lessons_count} lessons complete · {percent}%
                        </div>
                    )}
                </div>
                {!module.locked && (
                    <ChevronRight size={16} className={`flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`} color="var(--st-silver-500)" />
                )}
            </button>

            {open && !module.locked && (
                <ul className="pb-4 pl-14 space-y-1" data-testid={`module-lessons-${module.id}`}>
                    {module.lessons.map((l) => (
                        <li key={l.id}>
                            <Link
                                to={`/learn/lessons/${l.id}`}
                                className="flex items-center gap-3 py-2 px-3 rounded text-sm hover:bg-silver-50"
                                style={{ color: "var(--st-ink)", transition: "background-color 0.15s ease" }}
                                data-testid={`lesson-link-${l.id}`}
                            >
                                {l.progress?.completed ? (
                                    <CheckCircle2 size={14} strokeWidth={2} color="#067647" />
                                ) : (
                                    <PlayCircle size={14} strokeWidth={2} color="var(--st-royal)" />
                                )}
                                <span className="flex-1">{l.title}</span>
                                {l.duration_seconds ? (
                                    <span className="text-xs" style={{ color: "var(--st-silver-500)" }}>
                                        {Math.round(l.duration_seconds / 60)} min
                                    </span>
                                ) : null}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function PhaseCard({ phase, currentUserActive, autoOpenModuleId }) {
    const percent = (() => {
        const totals = phase.modules.reduce(
            (acc, m) => {
                acc.done += m.completed_lessons_count || 0;
                acc.total += m.total_lessons_count || 0;
                return acc;
            },
            { done: 0, total: 0 }
        );
        return totals.total ? Math.round((totals.done / totals.total) * 100) : 0;
    })();

    return (
        <div
            className="rounded-lg p-6"
            style={{
                background: "var(--st-white)",
                border: phase.unlocked ? "1px solid var(--st-line)" : "1px dashed var(--st-line)",
                opacity: phase.unlocked ? 1 : 0.75,
            }}
            data-testid={`phase-card-${phase.slug}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                        Phase {phase.order} · {phase.duration_label}
                    </div>
                    <h3 className="font-serif text-2xl mt-1" style={{ color: "var(--st-navy)" }}>{phase.name}</h3>
                </div>
                {phase.unlocked ? (
                    phase.completed ? (
                        <span className="tracking-caps text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: "#ECFDF3", color: "#067647" }}>
                            Complete
                        </span>
                    ) : (
                        <span className="tracking-caps text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: "#EEF2FE", color: "var(--st-royal)" }}>
                            In progress
                        </span>
                    )
                ) : (
                    <span className="inline-flex items-center gap-1 tracking-caps text-[10px] font-bold px-2 py-1 rounded-full"
                          style={{ background: "var(--st-silver-100)", color: "var(--st-ink-muted)" }}>
                        <Lock size={11} strokeWidth={2} /> Locked
                    </span>
                )}
            </div>

            {!phase.unlocked && (
                <p className="mt-3 text-sm" style={{ color: "var(--st-ink-muted)" }} data-testid={`phase-lock-note-${phase.slug}`}>
                    {phase.order === 2
                        ? "Complete Phase 1 and pass the checkpoint quiz to unlock."
                        : "Complete Phase 2 and pass the checkpoint quiz to unlock."}
                </p>
            )}

            {phase.unlocked && (
                <>
                    {/* Progress bar */}
                    <div className="mt-4">
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--st-silver-100)" }}>
                            <div
                                className="h-full rounded-full"
                                style={{ width: `${percent}%`, background: "var(--st-royal)", transition: "width 0.3s ease" }}
                                data-testid={`phase-progress-bar-${phase.slug}`}
                            />
                        </div>
                        <div className="mt-1 flex items-center justify-between text-xs" style={{ color: "var(--st-ink-muted)" }}>
                            <span>{percent}% complete</span>
                            <span>
                                {phase.modules.filter((m) => m.completed).length}/{phase.modules.length} modules
                            </span>
                        </div>
                    </div>

                    {/* Modules accordion */}
                    <div className="mt-4">
                        {phase.modules.map((m) => (
                            <ModuleRow
                                key={m.id}
                                module={m}
                                phase={phase}
                                currentUserActive={currentUserActive}
                                defaultOpen={m.id === autoOpenModuleId}
                            />
                        ))}
                    </div>

                    {/* Checkpoint quiz */}
                    {phase.quiz && (
                        <div className="mt-4 rounded-lg p-4"
                             style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }}
                             data-testid={`phase-quiz-block-${phase.slug}`}>
                            <div className="flex items-center gap-3">
                                <Award size={20} strokeWidth={1.6} color="var(--st-royal)" />
                                <div className="flex-1">
                                    <div className="font-serif text-base" style={{ color: "var(--st-navy)" }}>
                                        {phase.quiz.title}
                                    </div>
                                    <div className="text-xs mt-0.5" style={{ color: "var(--st-ink-muted)" }}>
                                        {phase.quiz.passed
                                            ? "Passed — next phase unlocked."
                                            : phase.completed
                                            ? "All modules complete. Ready to take the checkpoint."
                                            : "Complete every module in this phase, then take the checkpoint."}
                                    </div>
                                </div>
                                {phase.quiz.passed ? (
                                    <span className="tracking-caps text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: "#ECFDF3", color: "#067647" }}>
                                        Passed
                                    </span>
                                ) : (
                                    <Link
                                        to={`/learn/quizzes/${phase.quiz.id}`}
                                        className={phase.completed ? "btn-royal text-sm" : "btn-ghost text-sm pointer-events-none opacity-60"}
                                        data-testid={`phase-quiz-cta-${phase.slug}`}
                                    >
                                        {phase.completed ? "Start checkpoint" : "Locked"}
                                    </Link>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default function CurriculumOverview() {
    const { user } = useAuth();
    const [tree, setTree] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.getCurriculum()
            .then(setTree)
            .catch((err) => setError(err.status === 402 ? "inactive" : err.message));
    }, []);

    if (error === "inactive") {
        return (
            <div className="st-surface p-8" data-testid="curriculum-inactive-notice">
                <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                    Curriculum
                </div>
                <h2 className="font-serif text-2xl mt-2" style={{ color: "var(--st-navy)" }}>
                    Enroll to begin the curriculum
                </h2>
                <p className="mt-3 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Your account is active, but your subscription is not. Choose a plan on the landing page's
                    Enrollment section to unlock the 12-month program.
                </p>
                <Link to="/#enrollment" className="btn-royal text-sm mt-6 inline-block" data-testid="curriculum-view-plans">
                    View plans
                </Link>
            </div>
        );
    }

    if (error) {
        return (
            <div className="st-surface p-6" data-testid="curriculum-error">
                <p className="text-sm" style={{ color: "#B42318" }}>{error}</p>
            </div>
        );
    }

    if (!tree) {
        return (
            <div className="st-surface p-6" data-testid="curriculum-loading">
                <p className="text-sm" style={{ color: "var(--st-ink-muted)" }}>Loading curriculum…</p>
            </div>
        );
    }

    // Find current phase = first unlocked, incomplete phase; else first unlocked complete; else first.
    const currentPhase =
        tree.phases.find((p) => p.unlocked && !p.completed) ||
        tree.phases.find((p) => p.unlocked) ||
        tree.phases[0];

    // Auto-open the first non-completed module in the current phase.
    const autoOpenModuleId = (() => {
        if (!currentPhase?.unlocked) return null;
        const target = currentPhase.modules.find((m) => !m.locked && !m.completed);
        return target?.id || null;
    })();

    const nextLesson = (() => {
        if (!currentPhase?.unlocked) return null;
        for (const m of currentPhase.modules) {
            if (m.locked) continue;
            for (const l of m.lessons) {
                if (!l.progress?.completed) return l;
            }
        }
        return null;
    })();

    // Compute program-wide completion percentage.
    const totalLessons = tree.phases.reduce((acc, p) => acc + p.modules.reduce((a, m) => a + (m.total_lessons_count || 0), 0), 0);
    const completedLessons = tree.phases.reduce((acc, p) => acc + p.modules.reduce((a, m) => a + (m.completed_lessons_count || 0), 0), 0);
    const overallPct = totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0;

    return (
        <div className="space-y-8" data-testid="curriculum-overview">
            {/* Overall progress overview */}
            <div className="st-surface p-6 md:p-8 flex flex-col md:flex-row gap-8 items-start" data-testid="progress-overview-card">
                <div className="flex items-center gap-5 flex-shrink-0">
                    <div className="relative w-24 h-24">
                        <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
                            <circle cx="20" cy="20" r="16" fill="none" stroke="var(--st-silver-100)" strokeWidth="4" />
                            <circle
                                cx="20" cy="20" r="16" fill="none"
                                stroke="var(--st-royal)" strokeWidth="4"
                                strokeDasharray={`${(overallPct / 100) * (2 * Math.PI * 16)} ${2 * Math.PI * 16}`}
                                strokeLinecap="round"
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="font-serif text-2xl" style={{ color: "var(--st-navy)" }} data-testid="overall-progress-pct">
                                {overallPct}%
                            </span>
                            <span className="text-[10px] tracking-caps font-semibold" style={{ color: "var(--st-silver-500)" }}>
                                Program
                            </span>
                        </div>
                    </div>
                    <div>
                        <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                            Overall progress
                        </div>
                        <div className="font-serif text-xl mt-1" style={{ color: "var(--st-navy)" }}>
                            {completedLessons} / {totalLessons} lessons
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--st-ink-muted)" }}>
                            Across all 3 phases
                        </div>
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>
                        Current phase
                    </div>
                    <h2 className="font-serif text-2xl mt-1" style={{ color: "var(--st-navy)" }} data-testid="progress-current-phase">
                        {currentPhase.name}{" "}
                        <span className="text-sm font-sans font-normal" style={{ color: "var(--st-silver-500)" }}>
                            · {currentPhase.duration_label}
                        </span>
                    </h2>
                    {nextLesson ? (
                        <Link
                            to={`/learn/lessons/${nextLesson.id}`}
                            className="btn-royal text-sm mt-4 inline-block"
                            data-testid="curriculum-next-lesson-cta"
                        >
                            Continue: {nextLesson.title}
                        </Link>
                    ) : (
                        <p className="mt-3 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                            All lessons in this phase are complete. Take the checkpoint quiz to unlock the next phase.
                        </p>
                    )}
                </div>
            </div>

            {/* Phase cards */}
            <div className="space-y-6" data-testid="curriculum-phases">
                {tree.phases.map((p) => (
                    <PhaseCard
                        key={p.id}
                        phase={p}
                        currentUserActive={true}
                        autoOpenModuleId={p.id === currentPhase.id ? autoOpenModuleId : null}
                    />
                ))}
            </div>
        </div>
    );
}
